import { syncApiUrl } from "@cliqsuite/db/powersync";
import { saveProfile } from "./wallet";

// The signed-in guest account (sync-api routes/guest-account.ts): one sign-in for every business,
// by Telegram or by phone number. Signing in also brings the account's member cards onto this
// phone as tickets, which is what the wallet and each store's loyalty page already read.

const ACCOUNT_KEY = "cliqsuite_guest_account";

export interface GuestAccount {
  name: string;
  phone: string | null;
  /** An authenticator app is on: this number needs its code on another phone. */
  protected: boolean;
  telegram: boolean;
}

interface SessionReply {
  token: string;
  account: GuestAccount;
  cards: Array<{ memberId: string; businessId: string; ticket: string }>;
}

export function readAccount(): { token: string; account: GuestAccount } | null {
  try {
    const held = JSON.parse(localStorage.getItem(ACCOUNT_KEY) ?? "null") as { token?: string; account?: GuestAccount } | null;
    return held?.token && held.account ? { token: held.token, account: held.account } : null;
  } catch {
    return null;
  }
}

export const accountToken = () => readAccount()?.token;

export function forgetAccount() {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    // nothing stored
  }
}

/** Remembers a sign-in on this phone: the account, the profile stores join with, and a ticket per card. */
function adopt(reply: SessionReply): GuestAccount {
  try {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify({ token: reply.token, account: reply.account }));
    // Same key and shape session.tsx's signInAs writes.
    for (const c of reply.cards) localStorage.setItem(`cliqsuite_guest_member:${c.businessId}`, JSON.stringify({ memberId: c.memberId, ticket: c.ticket }));
  } catch {
    // private mode: signed in for this visit only
  }
  if (reply.account.phone) saveProfile({ name: reply.account.name, phone: reply.account.phone });
  return reply.account;
}

/** A failed call's `error` from the server ("wrong_code", "no_account", …), or "network". */
export class AccountError extends Error {}

async function call<T>(path: string, body?: unknown, signedIn = false): Promise<T> {
  const token = signedIn ? accountToken() : undefined;
  let res: Response;
  try {
    res = await fetch(syncApiUrl(`/auth/guest${path}`), {
      method: body === undefined ? "GET" : "POST",
      headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(30_000),
    });
  } catch {
    throw new AccountError("network");
  }
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new AccountError(res.status === 429 ? "slow_down" : (json.error ?? "failed"));
  return json;
}

/** Me, "Your details": a new name or phone number. Resolves "code" when a protected account must give its authenticator code to change the number. */
export async function saveDetails(input: { name: string; phone: string; code?: string }): Promise<GuestAccount | "code"> {
  const reply = await call<SessionReply & { needsCode?: boolean }>("/profile", input, true);
  return reply.needsCode ? "code" : adopt(reply);
}

/** The signed-in guest's bag at a store, as kept with their account. Null when nobody is signed in or it can't be reached. */
export type Bag<L> = { lines: L[]; note: string; at: string | null };
export async function loadBag<L>(storeId: string): Promise<Bag<L> | null> {
  if (!accountToken()) return null;
  return call<Bag<L>>(`/bag?storeId=${storeId}`, undefined, true).catch(() => null);
}
/** Saves the bag from the version `seen`. `conflict` is the bag another device saved meanwhile; nothing was written then. */
export async function saveBag<L>(storeId: string, lines: L[], note: string, seen: string | null): Promise<{ at: string | null; conflict?: Bag<L> } | null> {
  if (!accountToken()) return null;
  return call<{ at: string | null; conflict?: Bag<L> }>("/bag", { storeId, lines, note, seen }, true).catch(() => null);
}
/** The table the account is at in a store, so its other devices join it. null: the visit is over. */
export async function saveTable(storeId: string, tableId: string | null): Promise<void> {
  if (accountToken()) await call("/table", { storeId, tableId }, true).catch(() => undefined);
}

/** Sign in (no name) or create an account (with a name). Resolves "code" when the number needs its authenticator code. */
export async function withPhone(input: { phone: string; name?: string; code?: string }): Promise<GuestAccount | "code"> {
  const reply = await call<SessionReply & { needsCode?: boolean }>("/phone", input);
  return reply.needsCode ? "code" : adopt(reply);
}

export const telegramConfig = () => call<{ telegramBot: string | null; telegramBotId: string | null }>("/config");

export type TelegramData = Record<string, unknown>;

/** Opens Telegram's own sign-in window. Resolves null when the guest closes it, or the Telegram
 * reply itself when the account is protected: send it again with the code through `telegramWithCode`. */
export async function withTelegram(botId: string): Promise<GuestAccount | null | { needsCode: TelegramData }> {
  if (!window.Telegram?.Login) {
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://telegram.org/js/telegram-widget.js?22";
      s.onload = () => resolve();
      s.onerror = () => reject(new AccountError("network"));
      document.head.appendChild(s);
    });
  }
  const data = await new Promise<TelegramData | false>((resolve) => window.Telegram!.Login.auth({ bot_id: botId, request_access: true }, resolve));
  if (!data) return null;
  // Signed in already (Me, "Link"): the token makes the server attach Telegram to this account.
  const reply = await call<SessionReply & { needsCode?: boolean }>("/telegram", data, true);
  return reply.needsCode ? { needsCode: data } : adopt(reply);
}

export const telegramWithCode = async (data: TelegramData, code: string) => adopt(await call<SessionReply>("/telegram", { ...data, code }));
export const addPhone = async (phone: string) => adopt(await call<SessionReply>("/add-phone", { phone }, true));

export const refreshAccount = async () => adopt(await call<SessionReply>("/me", undefined, true));
export const totpSetup = () => call<{ secret: string; url: string }>("/totp/setup", {}, true);
export const totpEnable = async (code: string) => adopt(await call<SessionReply>("/totp/enable", { code }, true));

/** What to tell the guest for each way a sign-in call fails. */
export function accountErrorText(err: unknown): string {
  switch (err instanceof AccountError ? err.message : "") {
    case "wrong_code":
      return "That code isn't right. Check the app and try again.";
    case "no_account":
      return "There's no account with this number yet. Create one below.";
    case "account_exists":
      return "That number already has an account. Sign in instead.";
    case "invalid_phone":
      return "Enter your phone number.";
    case "telegram_taken":
      return "That Telegram is linked to another account.";
    case "slow_down":
      return "Too many tries. Wait a minute and try again.";
    case "network":
      return "Check your connection and try again.";
    default:
      return "Couldn't sign you in. Try again.";
  }
}

declare global {
  interface Window {
    Telegram?: { Login: { auth: (options: { bot_id: string; request_access?: boolean }, done: (data: Record<string, unknown> | false) => void) => void } };
  }
}
